import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, Button, List, Typography, Space, Spin, Alert, Avatar, Popconfirm } from 'antd';
import { SendOutlined, UserOutlined, RobotOutlined, AudioOutlined, PoweroffOutlined } from '@ant-design/icons';
import { useSharedData } from '../contexts/SharedDataContext';
import { v4 as uuid } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

function downsampleBuffer(buffer, srcRate, dstRate) {
    if (dstRate === srcRate) return buffer;
    const ratio = srcRate / dstRate;
    const length = Math.floor(buffer.length / ratio);
    const result = new Float32Array(length);
    for (let i = 0; i < length; i++) {
        result[i] = buffer[Math.floor(i * ratio)];
    }
    return result;
}

const VoiceAnalystChat = ({
                              sessionStarted,
                              isVisible,
                              audioEnabled = false,
                              onConnectionStatusChange,
                              onAnalystResponse,
                              onEndSession,
                              context = null,
                              initialMessage = "Hi! I'm your Call Analytics Expert. What would you like to know?",
                              height = '600px',
                              showCallModal = null,
                              showReportModal = null,
                              showCallsList = null
                          }) => {

    const audioEnabledRef = useRef(audioEnabled);
    useEffect(() => {
        audioEnabledRef.current = audioEnabled;

        // Send modality update to server when audio setting changes
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'update_modalities',
                audioEnabled: audioEnabled
            }));
        }
    }, [audioEnabled]);

    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentResponse, setCurrentResponse] = useState(''); // For assistant's live text delta (UI display)
    const [isRecording, setIsRecording] = useState(false);
    const [interimUserMessageId, setInterimUserMessageId] = useState(null);

    const wsRef = useRef(null);
    const sessionIdRef = useRef(uuid());
    const messagesEndRef = useRef(null);
    const currentResponseAccumulatorRef = useRef(''); // Internal ref for accumulating assistant text
    const connectionAttemptRef = useRef(false);
    const cleanupTimeoutRef = useRef(null);

    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const scriptProcessorRef = useRef(null);
    const audioQueueRef = useRef([]);
    const isPlayingAudioRef = useRef(false);
    const sourceNodesRef = useRef([]);
    const playTimeRef = useRef(0);
    const isRecordingRef = useRef(isRecording);

    const { selectedTenant } = useSharedData();
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();

    const TARGET_INPUT_SAMPLE_RATE = 16000;
    const TARGET_OUTPUT_SAMPLE_RATE = 24000;
    const FADE_TIME = 0.005;

    useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

    useEffect(() => {
        if (sessionStarted && messages.length === 0) {
            setMessages([{ id: uuid(), type: 'assistant', content: initialMessage, timestamp: new Date() }]);
        }
    }, [sessionStarted, initialMessage, messages.length]);

    const updateConnectionStatus = useCallback((status) => {
        setConnectionStatus(status);
        onConnectionStatusChange?.(status);
    }, [onConnectionStatusChange]);

    const safeStringify = useCallback((value) => {
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object') return value.message || value.error || JSON.stringify(value);
        return '' + value;
    }, []);

    const initAudioContext = useCallback(() => {
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            try {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            } catch (e) {
                console.error('Error initializing AudioContext:', e);
                setError('Could not initialize audio system.');
                audioContextRef.current = null;
                return false;
            }
        }
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume().catch(err => console.error('AudioContext resume error:', err));
        }
        return true;
    }, []);

    const cleanupAudio = useCallback(() => {
        if (isRecordingRef.current) {
            mediaStreamRef.current?.getTracks().forEach(t => t.stop());
            scriptProcessorRef.current?.disconnect();
            setIsRecording(false);
        }
        mediaStreamRef.current?.getTracks().forEach(t => t.stop());
        scriptProcessorRef.current?.disconnect();
        sourceNodesRef.current.forEach(src => { try { src.stop(); } catch {} });
        sourceNodesRef.current = [];
        audioQueueRef.current = [];
        isPlayingAudioRef.current = false;
        playTimeRef.current = 0;
    }, []);

    const processAudioQueue = useCallback(() => {
        const ctx = audioContextRef.current;
        if (!ctx || !audioEnabledRef.current || isPlayingAudioRef.current || audioQueueRef.current.length === 0) return;
        if (ctx.state !== 'running') return;

        isPlayingAudioRef.current = true;
        const buffer = audioQueueRef.current.shift();
        const dv = new DataView(buffer);
        const frameCount = dv.byteLength / 2;
        const int16 = new Int16Array(frameCount);
        for (let i = 0; i < frameCount; i++) int16[i] = dv.getInt16(i * 2, true);

        const float32 = new Float32Array(frameCount);
        for (let i = 0; i < frameCount; i++) float32[i] = int16[i] / 32768;

        const audioBuf = ctx.createBuffer(1, float32.length, TARGET_OUTPUT_SAMPLE_RATE);
        audioBuf.copyToChannel(float32, 0);

        const source = ctx.createBufferSource();
        source.buffer = audioBuf;

        const now = ctx.currentTime;
        const startAt = Math.max(playTimeRef.current, now);
        const endAt = startAt + audioBuf.duration;
        playTimeRef.current = endAt;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(1, startAt + FADE_TIME);
        gain.gain.setValueAtTime(1, endAt - FADE_TIME);
        gain.gain.linearRampToValueAtTime(0, endAt);

        source.connect(gain).connect(ctx.destination);
        source.onended = () => {
            isPlayingAudioRef.current = false;
            sourceNodesRef.current = sourceNodesRef.current.filter(s => s !== source);
            if (ctx.state === 'running') processAudioQueue();
        };
        sourceNodesRef.current.push(source);
        source.start(startAt);
    }, []);

    const handlePlayableAudioChunk = useCallback((base64Audio) => {
        if (!audioEnabledRef.current) return;
        if (!initAudioContext()) return;
        try {
            const binary = window.atob(base64Audio);
            const buffer = new ArrayBuffer(binary.length);
            const view = new Uint8Array(buffer);
            for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
            audioQueueRef.current.push(buffer);
            if (audioContextRef.current.state === 'running') processAudioQueue();
            else audioContextRef.current.resume().then(processAudioQueue);
        } catch (e) {
            console.error('Error processing chunk:', e);
        }
    }, [initAudioContext, processAudioQueue]);

    const stopRecording = useCallback(() => {
        if (!isRecordingRef.current) return;
        setIsRecording(false);
        mediaStreamRef.current?.getTracks().forEach(t => t.stop());
        scriptProcessorRef.current?.disconnect();
    }, []);

    useEffect(() => {
        if (sessionStarted && audioEnabledRef.current) initAudioContext();
        else { if (isRecordingRef.current) stopRecording(); cleanupAudio(); }
    }, [sessionStarted, initAudioContext, stopRecording, cleanupAudio]);

    useEffect(() => { if (connectionStatus !== 'connected' && isRecordingRef.current) stopRecording(); }, [connectionStatus, stopRecording]);

    // Helper to add text with proper spacing, smarter about punctuation
    const appendTextWithSpace = (currentText, newText, skipSpace = true) => {
        if(skipSpace) {
            if (!currentText || currentText === '🎤 Listening...') return newText;
            return currentText + newText
        }
        if (!newText || newText.trim() === '') return currentText;
        if (!currentText || currentText === '🎤 Listening...') return newText.trimStart();

        const lastCharCurrent = currentText.slice(-1);
        const firstCharNew = newText.charAt(0);

        const noSpaceBeforePunctuation = /^[.,?!:;'")]/;
        const noSpaceAfterPunctuation = /['""(\[]$/;

        if (lastCharCurrent === ' ' || firstCharNew === ' ') {
            return currentText + newText;
        }
        if (noSpaceBeforePunctuation.test(firstCharNew)) {
            return currentText + newText;
        }
        if (noSpaceAfterPunctuation.test(lastCharCurrent)) {
            return currentText + newText;
        }

        return currentText + ' ' + newText;
    };

    const startRecording = useCallback(async () => {
        if (isRecordingRef.current || !audioEnabledRef.current || connectionStatus !== 'connected') return;
        if (!initAudioContext()) { setError('Audio init failed'); return; }
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') await ctx.resume();
        if (ctx.state !== 'running') { setError('Audio not running'); return; }

        try {
            mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: { sampleRate: ctx.sampleRate, channelCount: 1, echoCancellation: true }, video: false });
            setIsRecording(true);

            let currentInterimId = null;
            setMessages(prev => {
                const lastMessage = prev[prev.length - 1];
                if (lastMessage && lastMessage.type === 'user' && lastMessage.__isInterimUser) {
                    currentInterimId = lastMessage.id;
                    return prev;
                } else {
                    const newId = uuid();
                    currentInterimId = newId;
                    return [...prev, {
                        id: newId,
                        type: 'user',
                        content: '🎤 Listening...',
                        timestamp: new Date(),
                        __isInterimUser: true
                    }];
                }
            });

            const sourceNode = ctx.createMediaStreamSource(mediaStreamRef.current);
            const procNode = ctx.createScriptProcessor(4096, 1, 1);
            scriptProcessorRef.current = procNode;

            procNode.onaudioprocess = (evt) => {
                if (!isRecordingRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
                const input = evt.inputBuffer.getChannelData(0);
                const down = downsampleBuffer(input, ctx.sampleRate, TARGET_INPUT_SAMPLE_RATE);
                const pcm16 = new Int16Array(down.length);
                for (let i = 0; i < down.length; i++) {
                    const s = Math.max(-1, Math.min(1, down[i]));
                    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                }
                const binStr = String.fromCharCode(...new Uint8Array(pcm16.buffer));
                wsRef.current.send(JSON.stringify({ type: 'audio_data', audio: window.btoa(binStr) }));
            };

            sourceNode.connect(procNode);
            const silentGain = ctx.createGain();
            silentGain.gain.setValueAtTime(0, ctx.currentTime);
            procNode.connect(silentGain);
            silentGain.connect(ctx.destination);
        } catch (err) {
            console.error('Error starting recording:', err);
            setError(err.name === 'NotAllowedError' ? 'Microphone access denied.' : 'Error starting mic.');
            setIsRecording(false);
            cleanupAudio();
        }
    }, [connectionStatus, initAudioContext, cleanupAudio, setIsRecording, setMessages, setError, audioEnabledRef]);

    const handleWebSocketMessage = useCallback((message) => {
        console.log('AI Analyst WS message:', message.type, message);
        try {
            switch (message.type) {
                case 'user_text_delta':
                    setMessages(prevMessages => {
                        const lastMessage = prevMessages[prevMessages.length - 1];
                        if (lastMessage && lastMessage.type === 'user' && lastMessage.__isInterimUser) {
                            const currentContent = lastMessage.content === '🎤 Listening...' ? '' : lastMessage.content;
                            return prevMessages.map(m =>
                                m.id === lastMessage.id
                                    ? { ...m, content: appendTextWithSpace(currentContent, message.text) }
                                    : m
                            );
                        } else {
                            const newId = uuid();
                            setInterimUserMessageId(newId);
                            return [...prevMessages, {
                                id: newId,
                                type: 'user',
                                content: message.text,
                                timestamp: new Date(),
                                __isInterimUser: true
                            }];
                        }
                    });
                    break;

                case 'user_text_done':
                    setIsLoading(true);
                    setMessages(prevMessages => prevMessages.map(m => {
                        const lastMessage = prevMessages[prevMessages.length -1];
                        if ((interimUserMessageId && m.id === interimUserMessageId) || (lastMessage && m.id === lastMessage.id && m.type ==='user' && m.__isInterimUser) ) {
                            return { ...m, content: message.text || m.content, __isInterimUser: false };
                        }
                        return m;
                    }));
                    setInterimUserMessageId(null);
                    break;

                case 'assistant_text_delta':
                    setIsLoading(false);
                    setMessages(prevMessages => {
                        const lastMessage = prevMessages[prevMessages.length -1];
                        if ((interimUserMessageId && prevMessages.some(m => m.id === interimUserMessageId && m.__isInterimUser)) || (lastMessage && lastMessage.type === 'user' && lastMessage.__isInterimUser)) {
                            return prevMessages.map(m =>
                                (m.id === interimUserMessageId || (m.id === lastMessage.id && m.type === 'user' && m.__isInterimUser))
                                    ? { ...m, __isInterimUser: false }
                                    : m
                            );
                        }
                        return prevMessages;
                    });
                    setInterimUserMessageId(null);

                    const newText = safeStringify(message.text || message.delta || '');
                    currentResponseAccumulatorRef.current = appendTextWithSpace(currentResponseAccumulatorRef.current, newText);
                    setCurrentResponse(currentResponseAccumulatorRef.current);
                    break;

                case 'audio_chunk':
                    setIsLoading(false);
                    setMessages(prevMessages => {
                        const lastMessage = prevMessages[prevMessages.length -1];
                        if ((interimUserMessageId && prevMessages.some(m => m.id === interimUserMessageId && m.__isInterimUser)) || (lastMessage && lastMessage.type === 'user' && lastMessage.__isInterimUser)) {
                            return prevMessages.map(m =>
                                (m.id === interimUserMessageId || (m.id === lastMessage.id && m.type === 'user' && m.__isInterimUser))
                                    ? { ...m, __isInterimUser: false }
                                    : m
                            );
                        }
                        return prevMessages;
                    });
                    setInterimUserMessageId(null);

                    if (message.audio && audioEnabledRef.current) {
                        handlePlayableAudioChunk(message.audio);
                    }
                    break;

                case 'assistant_text_done':
                    const finalText = message.text || currentResponseAccumulatorRef.current.trim();
                    if (finalText) {
                        const assistantMessage = {
                            id: uuid(), type: 'assistant',
                            content: finalText, timestamp: new Date()
                        };
                        setMessages(prev => [...prev, assistantMessage]);
                        onAnalystResponse?.(assistantMessage);
                    }
                    currentResponseAccumulatorRef.current = '';
                    setCurrentResponse('');
                    setIsLoading(false);
                    break;

                case 'connection_status':
                    if (message.status === 'connected') { updateConnectionStatus('connected'); setError(null); }
                    break;

                case 'modalities_updated':
                    console.log(`Modalities updated: ${message.audioEnabled ? 'text+audio' : 'text only'}`);
                    break;

                case 'show_call':
                    if (message.callId && showCallModal) {
                        showCallModal(message.callId);
                    }
                    if (message.callId) {
                        const systemMessage = {
                            id: uuid(),
                            type: 'system',
                            content: `Opening call details for call ${message.callId}`,
                            timestamp: new Date(),
                            actionType: 'show_call',
                            actionData: { callId: message.callId }
                        };
                        setMessages(prev => [...prev, systemMessage]);
                    }
                    break;

                case 'show_calls_list':
                    if (message.callIds && Array.isArray(message.callIds) && message.callIds.length > 0 && showCallsList) {
                        showCallsList(message.callIds, message.title, message.description);
                    }
                    if (message.callIds) {
                        const systemMessage = {
                            id: uuid(),
                            type: 'system',
                            content: `Showing ${message.callIds.length} calls: ${message.title || 'AI Selected Calls'}`,
                            timestamp: new Date(),
                            actionType: 'show_calls_list',
                            actionData: {
                                callIds: message.callIds,
                                title: message.title,
                                description: message.description
                            }
                        };
                        setMessages(prev => [...prev, systemMessage]);
                    }
                    break;

                case 'show_report':
                    if (message.reportData && showReportModal) {
                        console.log("Calling showReportModal");
                        showReportModal(message.reportData, message.title);
                    }
                    if (message.reportData) {
                        const systemMessage = {
                            id: uuid(),
                            type: 'system',
                            content: `Opening report: ${message.title || 'Data Analysis'}`,
                            timestamp: new Date(),
                            actionType: 'show_report',
                            actionData: {
                                reportData: message.reportData,
                                title: message.title
                            }
                        };
                        setMessages(prev => [...prev, systemMessage]);
                    }
                    break;

                case 'error':
                    const errorMessage = safeStringify(message.error || message.message || 'An unknown error');
                    console.error('AI Analyst WS error:', errorMessage);
                    setError(errorMessage);
                    setIsLoading(false);
                    if (isRecordingRef.current) stopRecording();
                    setMessages(prev => prev.filter(m => !m.__isInterimUser));
                    setInterimUserMessageId(null);
                    updateConnectionStatus('error');
                    break;

                default:
                    console.log('Unknown WebSocket message type:', message.type, message);
            }
        } catch (e) {
            console.error('Error handling WebSocket message:', e);
            setError('Error processing response from AI Analyst.');
            setIsLoading(false);
            if (isRecordingRef.current) stopRecording();
            setMessages(prev => prev.filter(m => !m.__isInterimUser));
            setInterimUserMessageId(null);
            updateConnectionStatus('error');
        }
    }, [
        updateConnectionStatus, onAnalystResponse, handlePlayableAudioChunk, stopRecording, safeStringify,showCallModal,
        interimUserMessageId, setMessages, setCurrentResponse, setIsLoading, setInterimUserMessageId, setError, audioEnabledRef
    ]);

    const connectToAnalyst = useCallback(async () => {
        if (connectionAttemptRef.current || connectionStatus === 'connected' || connectionStatus === 'connecting') return;
        connectionAttemptRef.current = true;
        updateConnectionStatus('connecting'); setError(null);
        if (audioEnabledRef.current) initAudioContext();

        try {
            let token = null;
            if (isAuthenticated) {
                try {
                    token = await getAccessTokenSilently();
                } catch (tokenError) {
                    setError('Authentication required');
                    updateConnectionStatus('error');
                    connectionAttemptRef.current = false;
                    return;
                }
            }

            const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = window.location.hostname === 'localhost' ? 'localhost:3001' : window.location.host;
            let url = `${proto}//${host}/web-agent/${sessionIdRef.current}?tenant=${encodeURIComponent(selectedTenant||'')}&agentId=call-analytics-expert`;

            if (token) {
                url += `&token=${encodeURIComponent(token)}`;
            }
            console.log('Connecting to:', url);
            const ws = new WebSocket(url);
            wsRef.current = ws;
            const to = setTimeout(() => { if (ws.readyState === WebSocket.CONNECTING) { ws.close(); connectionAttemptRef.current = false; } }, 10000);
            ws.onopen = () => { clearTimeout(to); setTimeout(() => { if (ws.readyState===WebSocket.OPEN) ws.send(JSON.stringify({ agentId:'call-analytics-expert', tenant:selectedTenant, context: context?.defaultPropertyName||context?.apartmentPropertyName||null, audioEnabled:audioEnabledRef.current })); },100); };
            ws.onmessage = e => { try { handleWebSocketMessage(JSON.parse(e.data)); } catch {} };
            ws.onerror = e => { clearTimeout(to); connectionAttemptRef.current=false; setError('Connection error'); updateConnectionStatus('error'); };
            ws.onclose = e => { clearTimeout(to); connectionAttemptRef.current=false; updateConnectionStatus('disconnected'); if(sessionStarted) setError(`Conn lost: ${e.reason||e.code}`); };
        } catch (err) {
            connectionAttemptRef.current=false; setError('WS init failed'); updateConnectionStatus('error');
        }
    }, [selectedTenant, connectionStatus, initAudioContext, updateConnectionStatus, handleWebSocketMessage, sessionStarted, context]);

    const cleanup = useCallback(() => {
        if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);
        if (wsRef.current) { if([WebSocket.CONNECTING,WebSocket.OPEN].includes(wsRef.current.readyState)) wsRef.current.close(1000,'cleanup'); wsRef.current=null; }
        connectionAttemptRef.current=false; updateConnectionStatus('disconnected');
    }, [updateConnectionStatus]);

    // Connect when session starts, cleanup when session ends
    useEffect(() => {
        if (sessionStarted) {
            const t=setTimeout(() => {
                if(['disconnected','error'].includes(connectionStatus)) connectToAnalyst();
            },300);
            return ()=>clearTimeout(t);
        } else {
            if(isRecordingRef.current) stopRecording();
            cleanup();
        }
    }, [sessionStarted, connectionStatus, connectToAnalyst, stopRecording, cleanup]);

    useEffect(() => {
        return () => {
            if (isRecordingRef.current) stopRecording();
            cleanup();
            cleanupAudio();
            const ctx = audioContextRef.current;
            if (ctx && ctx.state!=='closed') ctx.close().catch(console.error);
            audioContextRef.current = null;
        };
    }, [stopRecording, cleanup, cleanupAudio]);

    const sendMessage = useCallback(() => {
        if (!inputValue.trim() || connectionStatus!=='connected' || isLoading ) return;
        setMessages(prev => [...prev, { id:uuid(), type:'user', content:inputValue.trim(), timestamp:new Date() }]);
        setIsLoading(true);
        currentResponseAccumulatorRef.current=''; setCurrentResponse('');
        wsRef.current?.send(JSON.stringify({ type:'text_message', text:inputValue.trim() }));
        setInputValue('');
    }, [inputValue, connectionStatus, isLoading]);

    const handleKeyPress = useCallback(e => { if(e.key==='Enter'&&!e.shiftKey){e.preventDefault(); sendMessage();} }, [sendMessage]);

    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages, currentResponse]);

    // Auto-start recording when chat becomes visible and audio is enabled
    useEffect(() => {
        if (
            isVisible &&
            audioEnabled &&
            connectionStatus === 'connected' &&
            !isRecordingRef.current
        ) {
            console.log("Auto-starting recording: visible, audio enabled, connected, not already recording.");
            const startTimeout = setTimeout(() => {
                if (isVisible && audioEnabled && connectionStatus === 'connected' && !isRecordingRef.current) {
                    startRecording();
                }
            }, 100);

            return () => clearTimeout(startTimeout);
        }
    }, [isVisible, audioEnabled, connectionStatus, startRecording]);

    const handleEndSession = useCallback(() => {
        if (onEndSession) {
            onEndSession();
        }
    }, [onEndSession]);

    const handleReplayAction = useCallback((message) => {
        if (!message.actionType || !message.actionData) return;

        switch (message.actionType) {
            case 'show_call':
                if (message.actionData.callId && showCallModal) {
                    showCallModal(message.actionData.callId);
                }
                break;
            case 'show_calls_list':
                if (message.actionData.callIds && showCallsList) {
                    showCallsList(
                        message.actionData.callIds,
                        message.actionData.title,
                        message.actionData.description
                    );
                }
                break;
            case 'show_report':
                if (message.actionData.reportData && showReportModal) {
                    showReportModal(
                        message.actionData.reportData,
                        message.actionData.title
                    );
                }
                break;
            default:
                console.log('Unknown action type:', message.actionType);
        }
    }, [showCallModal, showCallsList, showReportModal]);

    const renderMessage = message => {
        if (message.type === 'system') {
            const isClickable = message.actionType && message.actionData;
            return (
                <List.Item key={message.id} style={{ padding:'8px 16px', border:'none' }}>
                    <div
                        style={{
                            textAlign: 'center',
                            color: isClickable ? '#1890ff' : '#6b7280',
                            fontStyle: 'italic',
                            fontSize: '12px',
                            padding: '4px 8px',
                            cursor: isClickable ? 'pointer' : 'default',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            backgroundColor: isClickable ? 'rgba(24, 144, 255, 0.05)' : 'transparent',
                            border: isClickable ? '1px dashed rgba(24, 144, 255, 0.3)' : 'none',
                            ...(isClickable && {
                                ':hover': {
                                    backgroundColor: 'rgba(24, 144, 255, 0.1)',
                                }
                            })
                        }}
                        onClick={isClickable ? () => handleReplayAction(message) : undefined}
                        onMouseEnter={(e) => {
                            if (isClickable) {
                                e.target.style.backgroundColor = 'rgba(24, 144, 255, 0.1)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (isClickable) {
                                e.target.style.backgroundColor = 'rgba(24, 144, 255, 0.05)';
                            }
                        }}
                        title={isClickable ? 'Click to replay this action' : undefined}
                    >
                        {message.content}
                        {isClickable && (
                            <span style={{
                                marginLeft: '4px',
                                fontSize: '10px',
                                opacity: 0.7
                            }}>
                                ↻
                            </span>
                        )}
                    </div>
                </List.Item>
            );
        }
        return (
            <List.Item key={message.id} style={{ padding:'12px 16px', border:'none' }}>
                <List.Item.Meta
                    avatar={<Avatar icon={message.type==='user'?<UserOutlined/>:<RobotOutlined/>} style={{ backgroundColor: message.type==='user'? '#1890ff':'#52c41a' }}/>}
                    title={<Text strong style={{ fontSize:14 }}>{message.type==='user'?'You':'AI Analyst'}</Text>}
                    description={
                        <>
                            <Paragraph style={{ margin:0, whiteSpace:'pre-wrap' }}>
                                {renderMessageContent(message.content, message.type)}
                            </Paragraph>
                            <Text type="secondary" style={{ fontSize:12 }}>
                                {message.timestamp.toLocaleTimeString()}
                            </Text>
                        </>
                    }
                />
            </List.Item>
        );
    };

    const renderMessageContent = useCallback((content, messageType) => {
        if (messageType !== 'assistant') {
            return content;
        }

        const callIdRegex = /(?:call\s+(?:id[:\s]*)?|id[:\s]+)([a-f0-9-]{8,})/gi;

        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = callIdRegex.exec(content)) !== null) {
            if (match.index > lastIndex) {
                parts.push(content.substring(lastIndex, match.index));
            }

            const callId = match[1];
            parts.push(
                <Button
                    key={`call-${callId}-${match.index}`}
                    type="link"
                    style={{ padding: 0, height: 'auto', fontSize: 'inherit' }}
                    onClick={() => showCallModal && showCallModal(callId)}
                >
                    {match[0]}
                </Button>
            );

            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < content.length) {
            parts.push(content.substring(lastIndex));
        }

        return parts.length > 0 ? parts : content;
    }, [showCallModal]);

    const isConnected = connectionStatus === 'connected';

    const connectRef = useRef(connectToAnalyst);
    useEffect(() => {
        connectRef.current = connectToAnalyst;
    }, [connectToAnalyst]);

    // Reconnect when tenant changes (only if session is active)
    useEffect(() => {
        cleanup();
        if (sessionStarted) {
            connectRef.current();
        }
    }, [selectedTenant, sessionStarted]);

    if (!sessionStarted) {
        return null; // Don't render anything if session hasn't started
    }

    return (
        <div style={{ height, display:'flex', flexDirection:'column', backgroundColor:'#fafafa' }}>
            {connectionStatus==='connecting' && <Alert message="Connecting to AI Analyst..." type="info" showIcon style={{ margin:16, marginBottom:8 }}/>}

            {error && (
                <Alert
                    message={typeof error === 'string' ? error : 'Connection error occurred'} type="error" closable onClose={() => setError(null)}
                    style={{ margin: '16px', marginBottom: '8px' }}
                    action={connectionStatus !== 'connecting' && connectionStatus !== 'connected' && (
                        <Button size="small" danger onClick={() => { setError(null); connectToAnalyst(); }}>Retry</Button>
                    )}
                />
            )}

            <div style={{ flex: 1, overflow: 'auto', backgroundColor: 'white', margin: '16px', marginBottom: '8px', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
                <List dataSource={messages} renderItem={renderMessage} style={{ padding: '8px 0' }} />
                {currentResponse && (
                    <List.Item style={{ padding: '12px 16px', border: 'none', alignItems: 'flex-start', backgroundColor: '#f6ffed' }}>
                        <List.Item.Meta
                            avatar={<Avatar icon={<RobotOutlined />} style={{ backgroundColor: '#52c41a' }} />}
                            title={<Text strong style={{ fontSize: '14px' }}>AI Analyst</Text>}
                            description={
                                <div style={{ marginTop: '4px' }}>
                                    <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                        {currentResponse}<span style={{ animation: 'blink 1s infinite', fontWeight: 'bold' }}>|</span>
                                    </Paragraph>
                                </div>
                            }
                        />
                    </List.Item>
                )}
                {isLoading && !currentResponse && (
                    <div style={{ padding: '16px', textAlign: 'center' }}>
                        <Spin size="small" /><Text type="secondary" style={{ marginLeft: '8px' }}>AI Analyst is thinking...</Text>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div style={{ padding: '16px', backgroundColor: 'white', borderTop: '1px solid #d9d9d9' }}>
                <Space.Compact style={{ width: '100%' }}>
                    <TextArea
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder={isConnected ? (isRecording ? "Listening..." : "Ask me about your call data...") : "Connecting to AI Analyst..."}
                        disabled={!isConnected || isLoading}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        style={{ resize: 'none' }}
                    />
                    {audioEnabled && (
                        <Button
                            icon={<AudioOutlined />}
                            onClick={isRecording ? stopRecording : startRecording}
                            disabled={!isConnected || isLoading}
                            danger={isRecording}
                            type={isRecording ? "primary" : "default"}
                            style={{ height: 'auto' }}
                        />
                    )}
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={sendMessage}
                        disabled={!isConnected || !inputValue.trim() || isLoading }
                        style={{ height: 'auto' }}
                    />
                </Space.Compact>

                <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Session: {sessionIdRef.current.substring(0, 8)}... | Status: {connectionStatus}
                        {audioEnabled && isRecording && ' (Recording)'}
                    </Text>

                    <Popconfirm
                        title="End AI Session"
                        description="This will close the connection and clear the chat history. Are you sure?"
                        onConfirm={handleEndSession}
                        okText="Yes, End Session"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<PoweroffOutlined />}
                            style={{ fontSize: '11px' }}
                        >
                            End Session
                        </Button>
                    </Popconfirm>
                </div>
            </div>
            <style>{`@keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }`}</style>
        </div>
    );
};

export default VoiceAnalystChat;
